import type { CarrierShipmentStatus } from '@/lib/api/carrier-shipment-api'
import { m } from '@/lib/paraglide/messages'

/**
 * How a carrier state should read to a human. `progress` is "still moving",
 * `success` is delivered, `warning` is an outcome that needs attention (the
 * parcel came back, the carrier is waiting on us), `danger` is a failure or a
 * cancellation.
 */
export type CarrierTone =
    'neutral' | 'progress' | 'success' | 'warning' | 'danger'

export interface CarrierDisplay {
    label: string
    tone: CarrierTone
}

/** Carrier status code the carrier is holding the parcel on, awaiting our reply. */
const RETURN_REQUESTED_CODE = '505'

// The carrier's verified status table. Kept here rather than derived from the
// transit stage because the stage cannot express an outcome: the shipment row
// only ever holds transit states, so a delivered parcel still reads in_transit.
const STATUS_TABLE: Record<string, CarrierDisplay> = {
    '100': { label: m.carrier_status_100(), tone: 'progress' },
    '101': { label: m.carrier_status_101(), tone: 'danger' },
    '102': { label: m.carrier_status_102(), tone: 'warning' },
    '103': { label: m.carrier_status_103(), tone: 'progress' },
    '104': { label: m.carrier_status_104(), tone: 'progress' },
    '107': { label: m.carrier_status_107(), tone: 'danger' },
    '200': { label: m.carrier_status_200(), tone: 'progress' },
    '201': { label: m.carrier_status_201(), tone: 'danger' },
    '202': { label: m.carrier_status_202(), tone: 'progress' },
    '300': { label: m.carrier_status_300(), tone: 'progress' },
    '400': { label: m.carrier_status_400(), tone: 'progress' },
    '500': { label: m.carrier_status_500(), tone: 'progress' },
    '501': { label: m.carrier_status_501(), tone: 'success' },
    '502': { label: m.carrier_status_502(), tone: 'warning' },
    '503': { label: m.carrier_status_503(), tone: 'danger' },
    '504': { label: m.carrier_status_504(), tone: 'warning' },
    '505': { label: m.carrier_status_505(), tone: 'warning' },
    '506': { label: m.carrier_status_506(), tone: 'danger' },
    '507': { label: m.carrier_status_507(), tone: 'progress' },
    '508': { label: m.carrier_status_508(), tone: 'progress' },
    '509': { label: m.carrier_status_509(), tone: 'progress' },
    '515': { label: m.carrier_status_515(), tone: 'warning' },
    '550': { label: m.carrier_status_550(), tone: 'progress' },
}

const STAGE_TABLE: Record<CarrierShipmentStatus, CarrierDisplay> = {
    pending: { label: m.carrier_stage_pending(), tone: 'neutral' },
    created: { label: m.carrier_stage_created(), tone: 'progress' },
    picked_up: { label: m.carrier_stage_picked_up(), tone: 'progress' },
    in_transit: { label: m.carrier_stage_in_transit(), tone: 'progress' },
    cancelled: { label: m.carrier_stage_cancelled(), tone: 'danger' },
}

/** The subset of a shipment the display needs. */
export interface CarrierStateInput {
    status: CarrierShipmentStatus
    carrier_status_code?: string
}

/**
 * What the parcel's state should say on screen. The carrier's own code wins
 * whenever it is known — the transit stage cannot express "delivered" or
 * "returned", so reading the stage alone would claim a finished parcel is
 * still on its way.
 */
export function carrierShipmentDisplay(
    shipment: CarrierStateInput,
): CarrierDisplay {
    const code = shipment.carrier_status_code
    if (code) {
        return (
            STATUS_TABLE[code] ?? {
                label: m.carrier_status_unknown(),
                tone: 'progress',
            }
        )
    }
    return STAGE_TABLE[shipment.status]
}

/** Label for one push in the timeline. */
export function carrierEventLabel(code: string): string {
    return STATUS_TABLE[code]?.label ?? m.carrier_status_unknown()
}

/**
 * The carrier is holding the parcel until we approve the return or ask for a
 * redelivery. Nothing moves on its own, so this has to be visible.
 */
export function isCarrierReturnRequested(shipment: CarrierStateInput): boolean {
    return shipment.carrier_status_code === RETURN_REQUESTED_CODE
}

/**
 * A shipment claimed locally that the carrier never acknowledged (no waybill).
 * The backend lets dispatch run again on such a row, so the order page must
 * offer the booking form instead of a tracking panel that can neither cancel
 * (state unknown) nor retry.
 */
export function isCarrierShipmentRetryable(shipment: {
    status: CarrierShipmentStatus
    tracking_code?: string | null
}): boolean {
    return shipment.status === 'pending' && !shipment.tracking_code
}

// Chỉ những mã dịch vụ cộng thêm có tên trong tài liệu chính thức của VTP
// (partner2.viettelpost.vn: danh sách dịch vụ, FAQ). Mã khác VTP gửi về
// (GKN, PXD, COD…) chưa có nghĩa được xác minh nên không hiển thị.
const SERVICE_ADD_LABELS: Record<string, () => string> = {
    XMG: m.carrier_service_add_xmg,
    GNG: m.carrier_service_add_gng,
    GGD: m.carrier_service_add_ggd,
    GBP: m.carrier_service_add_gbp,
}

/** Tên các dịch vụ cộng thêm đã biết; mã chưa rõ nghĩa bị bỏ qua. */
export function carrierServiceAddLabels(codes: string[]): string[] {
    return codes.flatMap((code) => {
        const label = SERVICE_ADD_LABELS[code.trim().toUpperCase()]
        return label ? [label()] : []
    })
}

const ORDER_PAYMENT_LABELS: Record<number, () => string> = {
    1: m.carrier_order_payment_1,
    2: m.carrier_order_payment_2,
    3: m.carrier_order_payment_3,
    4: m.carrier_order_payment_4,
}

/** Nghĩa của `ORDER_PAYMENT` VTP; giá trị lạ trả undefined để màn ẩn dòng. */
export function carrierOrderPaymentLabel(
    value: number | null | undefined,
): string | undefined {
    return value == null ? undefined : ORDER_PAYMENT_LABELS[value]?.()
}

// Vận đơn đã huỷ (VTP huỷ lấy / đối tác huỷ / VTP huỷ / tiêu huỷ) không còn đi
// tiếp: gắn vào đơn chỉ tạo một vận đơn chết, server cũng từ chối (M-193).
const CANCELLED_WAYBILL_CODES = new Set(['101', '107', '201', '503'])

/** Vận đơn VTP tạo trên web VTP còn gắn được vào đơn ERP (chưa có đơn, chưa huỷ). */
export function isWaybillLinkable(waybill: {
    order_id?: string | null
    carrier_status_code: string
}): boolean {
    return (
        !waybill.order_id &&
        !CANCELLED_WAYBILL_CODES.has(waybill.carrier_status_code)
    )
}
